import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface DVLACheck {
  id: string;
  business_id: string;
  business_name: string;
  user_id: string | null;
  user_name: string | null;
  staff_member_id: string | null;
  staff_member_name: string | null;
  safety_check_id: string | null;
  safety_check_type: string | null;
  driving_license_number: string;
  check_date: string;
  check_status: 'pending' | 'completed' | 'failed' | 'error' | 'skipped';
  driver_name: string | null;
  driver_dob: string | null;
  license_status: string | null;
  points_total: number;
  is_staff_check: boolean;
  check_source: string;
  created_at: string;
}

interface Business {
  business_id: string;
  business_name: string;
}

interface DVLACheckStats {
  totalChecks: number;
  completedChecks: number;
  failedChecks: number;
  totalCreditsUsed: number;
  checksThisMonth: number;
}

const DVLAAuditLogs: React.FC = () => {
  const [checks, setChecks] = useState<DVLACheck[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [stats, setStats] = useState<DVLACheckStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [selectedBusiness, setSelectedBusiness] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [checkTypeFilter, setCheckTypeFilter] = useState<string>('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<DVLACheck | null>(null);

  const fetchBusinesses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBusinesses(response.data || []);
    } catch (err: any) {
      console.error('Error fetching businesses:', err);
    }
  }, []);

  const fetchDVLAChecks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      if (selectedBusiness) params.append('business_id', selectedBusiness);
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (checkTypeFilter) params.append('check_type', checkTypeFilter);
      params.append('page', (page + 1).toString());
      params.append('limit', rowsPerPage.toString());

      const response = await axios.get(`${API_BASE_URL}/owner/dvla-audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setChecks(response.data.checks || []);
      setStats(response.data.stats || null);
    } catch (err: any) {
      console.error('Error fetching DVLA checks:', err);
      setError('Failed to fetch DVLA audit logs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, statusFilter, dateFrom, dateTo, checkTypeFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  useEffect(() => {
    fetchDVLAChecks();
  }, [fetchDVLAChecks]);

  const handleViewDetail = (check: DVLACheck) => {
    setSelectedCheck(check);
    setDetailDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      if (selectedBusiness) params.append('business_id', selectedBusiness);
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (checkTypeFilter) params.append('check_type', checkTypeFilter);
      params.append('export', 'true');

      const response = await axios.get(`${API_BASE_URL}/owner/dvla-audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dvla-audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting data:', err);
      setError('Failed to export data. Please try again later.');
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': case 'error': return 'error';
      case 'pending': return 'warning';
      case 'skipped': return 'info';
      default: return 'default';
    }
  };

  const clearFilters = () => {
    setSelectedBusiness('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setCheckTypeFilter('');
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            DVLA Credit Usage & Audit Logs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchDVLAChecks} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '800px' }}>
          Complete audit trail of all DVLA credit usage across your businesses. This includes checks performed in the mobile app, 
          during loan agreements, from web admin, and staff management checks. Each completed check consumes 1 credit.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {stats.totalChecks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Checks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.completedChecks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                  {stats.failedChecks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.totalCreditsUsed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Credits Used
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {stats.checksThisMonth}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This Month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
          <Button
            variant="text"
            size="small"
            onClick={clearFilters}
            sx={{ ml: 'auto' }}
          >
            Clear All
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Business</InputLabel>
              <Select
                value={selectedBusiness}
                onChange={(e) => setSelectedBusiness(e.target.value)}
                label="Business"
              >
                <MenuItem value="">All Businesses</MenuItem>
                {businesses.map((business) => (
                  <MenuItem key={business.business_id} value={business.business_id}>
                    {business.business_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Check Type</InputLabel>
              <Select
                value={checkTypeFilter}
                onChange={(e) => setCheckTypeFilter(e.target.value)}
                label="Check Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="staff">Staff Check</MenuItem>
                <MenuItem value="safety">Safety Check</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              type="date"
              label="Date From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              type="date"
              label="Date To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ width: '100%', mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: 'background.default' }}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Check Source</TableCell>
                <TableCell>License Number</TableCell>
                <TableCell>Driver Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Points</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : checks.length > 0 ? (
                checks.map((check) => (
                  <TableRow key={check.id}>
                    <TableCell>
                      {new Date(check.created_at).toLocaleDateString('en-GB')}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(check.created_at).toLocaleTimeString('en-GB')}
                      </Typography>
                    </TableCell>
                    <TableCell>{check.business_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={check.check_source}
                        variant="outlined"
                        size="small"
                        color={
                          check.check_source === 'Staff Management' ? 'primary' :
                          check.check_source === 'Loan Check' ? 'secondary' :
                          check.check_source === 'Safety Check' ? 'info' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {check.driving_license_number}
                      </Typography>
                    </TableCell>
                    <TableCell>{check.driver_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={check.check_status.charAt(0).toUpperCase() + check.check_status.slice(1)}
                        color={getStatusColor(check.check_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-block', 
                        px: 1, 
                        py: 0.5, 
                        borderRadius: 1,
                        bgcolor: check.points_total > 6 ? 'error.light' : check.points_total > 3 ? 'warning.light' : 'success.light',
                        color: check.points_total > 6 ? 'error.dark' : check.points_total > 3 ? 'warning.dark' : 'success.dark'
                      }}>
                        {check.points_total}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={check.check_status === 'completed' ? '1 Credit' : '0 Credits'}
                        size="small"
                        color={check.check_status === 'completed' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{check.user_name || 'System'}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetail(check)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    No DVLA credit usage found. DVLA checks from all sources (Staff Management, Loan Checks, Safety Checks, Web Admin) will appear here once performed.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={-1} // Unknown total, server-side pagination
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          DVLA Check Details
        </DialogTitle>
        <DialogContent>
          {selectedCheck && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Check Information</Typography>
                <Typography variant="body2"><strong>Check ID:</strong> {selectedCheck.id}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {new Date(selectedCheck.created_at).toLocaleString()}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {selectedCheck.check_status}</Typography>
                <Typography variant="body2"><strong>Check Source:</strong> {selectedCheck.check_source}</Typography>
                <Typography variant="body2"><strong>Credits Used:</strong> {selectedCheck.check_status === 'completed' ? '1' : '0'}</Typography>
                <Typography variant="body2"><strong>Business:</strong> {selectedCheck.business_name}</Typography>
                <Typography variant="body2"><strong>Performed By:</strong> {selectedCheck.user_name || 'System'}</Typography>
                {selectedCheck.staff_member_name && (
                  <Typography variant="body2"><strong>Staff Member:</strong> {selectedCheck.staff_member_name}</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Driver Information</Typography>
                <Typography variant="body2"><strong>License Number:</strong> {selectedCheck.driving_license_number}</Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedCheck.driver_name || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Date of Birth:</strong> {selectedCheck.driver_dob ? new Date(selectedCheck.driver_dob).toLocaleDateString() : 'N/A'}</Typography>
                <Typography variant="body2"><strong>License Status:</strong> {selectedCheck.license_status || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Points Total:</strong> {selectedCheck.points_total}</Typography>
              </Grid>
              {selectedCheck.safety_check_id && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Related Check</Typography>
                  <Typography variant="body2"><strong>Safety/Loan Check ID:</strong> {selectedCheck.safety_check_id}</Typography>
                  {selectedCheck.safety_check_type && (
                    <Typography variant="body2"><strong>Check Type:</strong> {selectedCheck.safety_check_type}</Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DVLAAuditLogs;

